import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InactivityService } from '../inactivity/inactivity.service';
import { AuthService } from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class SessionManagementService {
    public isLoggingOut: boolean = false;
    public showWarningModal: boolean = false;
    public remainingSeconds: number = 0;
    private readonly MAX_TABS = 5;

    private destroy$ = new Subject<void>();
    private tabId: string = this.generateTabId();

    constructor(
        private inactivityService: InactivityService,
        private authService: AuthService
    ) { }

    private generateTabId(): string {
        let tabId = sessionStorage.getItem('tabId');
        if (!tabId) {
            tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem('tabId', tabId);
        }
        return tabId;
    }

    public initializeForUser(): void {
        this.showWarningModal = false;
        this.remainingSeconds = 0;
        this.setupInactivityTracking();
    }

    public stopSessionManagement(): void {
        this.inactivityService.stopTracking();
        this.showWarningModal = false;
    }

    private setupInactivityTracking(): void {
        this.inactivityService.warning$
            .pipe(takeUntil(this.destroy$))
            .subscribe((secondsRemaining) => {
                if (secondsRemaining > 0) {
                    this.showWarningModal = true;
                    this.remainingSeconds = Math.ceil(secondsRemaining);
                } else {
                    this.showWarningModal = false;
                    this.remainingSeconds = 0;
                }
            });

        this.inactivityService.logout$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.showWarningModal = false;
                this.remainingSeconds = 0;
                this.performLogout();
            });

        this.inactivityService.startTracking();
    }

    public onStayLoggedIn(): void {
        this.authService.signinSilent().then((success) => {
            if (success) {
                this.inactivityService.resetInactivity();
                this.inactivityService.broadcastStayLoggedIn();
            } else {
                this.performLogout();
            }
        }).catch(() => {
            this.performLogout();
        });
    }

    public onLogoutNow(): void {
        this.performLogout();
    }

    private performLogout(): void {
        this.isLoggingOut = true;

        localStorage.removeItem('session_logout_time');
        localStorage.removeItem('session_warning_active');
        localStorage.removeItem('session_last_activity_time');

        this.inactivityService.stopTracking();

        const tabIndex = this.getTabIndex();
        const delay = tabIndex * 1000;

        setTimeout(() => {
            try {
                this.authService.logout();
            } catch (error) {
                console.error('Logout error:', error);
                window.location.reload();
            }
        }, delay);
    }

    private getTabIndex(): number {
        const match = this.tabId.match(/(\d+)/);
        return match ? parseInt(match[0]) % this.MAX_TABS : 0;

    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}